import { useQueryClient } from '@tanstack/react-query';
import { useQuoteParams } from '@/features/quotes/context/quote-params.context';
import { mapRiskDataMapToPayload } from '@/features/quotes/components/capital-global/steps/utils/map-risk-values-to-payload.util';
import { markPendingStep3Resync } from '@/features/quotes/components/capital-global/steps/utils/step3-resync.util';
import type {
  EntityTab,
  RiskDataMap,
} from '@/features/quotes/components/capital-global/types';
import {
  collectMappedFieldErrorMessages,
  mapAttachValidationToRiskFieldErrors,
  type MapAttachValidationToRiskFieldErrorsResult,
} from '@/features/quotes/utils/map-attach-validation-to-risk-field-errors.util';
import { ValidationError } from '@/shared/exceptions/validation.error';
import { toaster } from '@/shared/ui/toaster.component';
import { useUpdateCapitalGlobalQuote } from './use-update-capital-global-quote.hook';

const RISK_BUSINESS_STEP_CODE = 'RISK_BUSINESS';

type Params = {
  onNext: () => void;
  captureAllEntities: () => RiskDataMap;
  entities: EntityTab[];
  onInvalidEntity: (entityId: string) => void;
  onAttachValidationError?: (
    result: MapAttachValidationToRiskFieldErrorsResult
  ) => void;
};

export function useSubmitCapitalGlobalRiskBusiness({
  onNext,
  captureAllEntities,
  entities,
  onInvalidEntity,
  onAttachValidationError,
}: Params) {
  const { policyId, id } = useQuoteParams();
  const resolvedPolicyId = (policyId || id || '').trim();
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useUpdateCapitalGlobalQuote();

  async function handleSubmit() {
    if (!resolvedPolicyId) {
      toaster.create({
        type: 'error',
        title: 'Dados incompletos',
        description: 'ID da cotação não encontrado. Volte e tente novamente.',
      });
      return;
    }

    const riskDataMap = captureAllEntities();

    const entityIds =
      entities.length > 0 ? entities.map(e => e.id) : Object.keys(riskDataMap);

    const riskProfileList = mapRiskDataMapToPayload(
      entityIds.length > 0
        ? Object.fromEntries(entityIds.map(id => [id, riskDataMap[id] ?? {}]))
        : riskDataMap,
      entities
    );

    try {
      const result = await mutateAsync({
        id: resolvedPolicyId,
        payload: {
          step: RISK_BUSINESS_STEP_CODE,
          riskProfileList,
        },
      });

      if (result.success) {
        await queryClient.refetchQueries({
          queryKey: ['proposal-details', resolvedPolicyId],
        });
        markPendingStep3Resync(resolvedPolicyId);
        onNext();
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        const mapped = mapAttachValidationToRiskFieldErrors(error, {
          entities,
          riskDataMap: riskDataMap,
        });
        onAttachValidationError?.(mapped);

        if (mapped.firstInvalidEntityId) {
          onInvalidEntity(mapped.firstInvalidEntityId);
        } else {
          const firstFieldKey = Object.keys(error.fieldErrors ?? {})[0];
          if (firstFieldKey) {
            const seqMatch = firstFieldKey.match(/riskProfileList\[(\d+)\]/);
            if (seqMatch) {
              const sequenceNumber = Number(seqMatch[1]);
              const tab = entities.find(
                e => e.sequenceNumber === sequenceNumber
              );
              if (tab) onInvalidEntity(tab.id);
            }
          }
        }

        const fieldErrorMessages = collectMappedFieldErrorMessages(
          mapped.entityErrors
        );

        const description =
          fieldErrorMessages.length > 0
            ? fieldErrorMessages.join('\n')
            : mapped.generalMessage?.trim() ||
              error.detail?.trim() ||
              (error.messages.length
                ? error.messages.join('\n')
                : error.message);

        toaster.create({
          type: 'error',
          title: 'Erro de validação',
          description,
        });
        return;
      }

      toaster.create({
        type: 'error',
        title: 'Não foi possível salvar',
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    }
  }

  return { handleSubmit, isPending };
}
